import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useTerminology } from '@/lib/useTerminology'
import { api } from '@/lib/axios'

interface ProjectCostLine {
  belegId: string
  belegNummer: string
  belegDatum: string
  lieferantName: string
  bezeichnung: string
  menge: number | null
  einheit: string | null
  betrag: number
  inFreigabe: boolean
  projectPositionId: string | null
  positionNumber: number | null
  positionName: string | null
}
interface ProjectCosts {
  lines: ProjectCostLine[]
  total: number
  inApproval: number
}
const fetchProjectCosts = (projectId: string) => api.get<{ data: ProjectCosts }>(`/incoming-invoices/project-costs/${projectId}`).then((r) => r.data.data)
const tagText = (iso: string | null): string => (iso ? iso.slice(0, 10).split('-').reverse().join('.') : '')
const mengeText = (n: number): string => n.toLocaleString('de-DE', { maximumFractionDigits: 3 })

/**
 * AP-34 14.3 — Reiter **„Eingangsrechnungen"** am Projekt: die Zeilen, die auf dieses Projekt
 * kontiert sind. Kosten ist, was freigegeben oder übergeben ist; Rechnungen im Freigabelauf stehen
 * dabei und zählen nicht — dieselbe Quelle, aus der die Nachkalkulation liest.
 *
 * Liegt unter `components/`, weil die Projektseite ihn einbindet: Features importieren nicht quer.
 * Deshalb trägt die Datei Abruf und Typen selbst.
 */
export function ProjektKosten({ projectId }: { projectId: string }) {
  const terms = useTerminology()
  const [kosten, setKosten] = useState<ProjectCosts | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  useEffect(() => {
    let aus = false
    setKosten(null)
    fetchProjectCosts(projectId)
      .then((k) => !aus && setKosten(k))
      .catch((e) => !aus && setFehler(apiErrorText(e, 'Die Eingangsrechnungen ließen sich nicht laden.')))
    return () => {
      aus = true
    }
  }, [projectId])

  if (fehler) return <div className="text-sm text-destructive">{fehler}</div>
  if (!kosten) return <p className="text-sm text-muted-foreground">Laden…</p>
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-1.5 font-medium">Eingangsnummer</th>
              <th className="px-4 py-1.5 font-medium">Rechnungsdatum</th>
              <th className="px-4 py-1.5 font-medium">Lieferant</th>
              <th className="px-4 py-1.5 font-medium">{terms.position}</th>
              <th className="px-4 py-1.5 font-medium">Bezeichnung</th>
              <th className="px-4 py-1.5 text-right font-medium">Menge</th>
              <th className="px-4 py-1.5 text-right font-medium">Betrag</th>
              <th className="px-4 py-1.5 font-medium">Stand</th>
            </tr>
          </thead>
          <tbody>
            {kosten.lines.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  Noch keine Eingangsrechnung auf dieses {terms.project}.
                </td>
              </tr>
            )}
            {kosten.lines.map((z, i) => (
              <tr key={`${z.belegId}-${i}`} className={`border-b border-border last:border-0 ${i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}>
                <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
                  <Link to={`/incoming-invoices/${z.belegId}`} className="text-primary hover:underline">
                    {z.belegNummer}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-4 py-1.5">{tagText(z.belegDatum || null)}</td>
                <td className="px-4 py-1.5">{z.lieferantName}</td>
                <td className="px-4 py-1.5">{z.positionNumber !== null ? `${z.positionNumber} ${z.positionName ?? ''}` : ''}</td>
                <td className="px-4 py-1.5">{z.bezeichnung}</td>
                <td className="whitespace-nowrap px-4 py-1.5 text-right tabular-nums">{z.menge !== null ? `${mengeText(z.menge)} ${z.einheit ?? ''}` : ''}</td>
                <td className={`whitespace-nowrap px-4 py-1.5 text-right tabular-nums ${z.inFreigabe ? 'text-muted-foreground' : ''}`}>{geldText(z.betrag)}</td>
                <td className="px-4 py-1.5">{z.inFreigabe && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs text-amber-700 dark:text-amber-300">in Freigabe</span>}</td>
              </tr>
            ))}
            {kosten.lines.length > 0 && (
              <tr className="border-t-2 border-border bg-muted/50 font-medium">
                <td className="px-4 py-1.5 text-muted-foreground" colSpan={6}>
                  {kosten.lines.length} Einträge
                </td>
                <td className="px-4 py-1.5 text-right tabular-nums">{geldText(kosten.total)}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {kosten.inApproval > 0 && <p className="border-t border-border px-4 py-2 text-sm text-muted-foreground">Dazu {geldText(kosten.inApproval)} in Freigabe — zählt noch nicht.</p>}
    </div>
  )
}
